import type {
  AppComponents,
  AppConnectorHosting,
} from "@p0security/integrations-app-shared/components";
import { awsPartitionFromUse } from "@p0security/integrations-aws-shared/gov-cloud";
import { awsRoleName } from "@p0security/integrations-aws-shared/policy/shared";
import type { AwsIntegration } from "@p0security/integrations-aws-shared/types";
import type { AzureIntegration } from "@p0security/integrations-azure-shared/types";
import {
  cloudRunViewerShell,
  cloudRunViewerTf,
} from "@p0security/integrations-gcloud-shared/cloud-run-iac";
import type { GcloudIntegration } from "@p0security/integrations-gcloud-shared/types";
import {
  type FrontendInstaller,
  otherInstalledIntegration,
} from "@p0security/integrations-installer";
import { azureConnectorTrustTf } from "@p0security/integrations-vaulted-credential-shared/iac";
import { SINGLETON_ITEM } from "@p0security/shared/install/constants";
import { installedItem } from "@p0security/shared/install/installed";
import type {
  ElementConfigOf,
  Instructions,
} from "@p0security/shared/install/types";
import { Typography } from "@p0security/ui";
import type { ReactNode } from "react";

const { Link, Paragraph, Text } = Typography;

const TERRAFORM_GUIDE_URL =
  "https://docs.p0.dev/integrations/resource-integrations/custom-application/deploying-a-connector-with-terraform";

// Shown above the Terraform for the invoke grant. The connector deployment itself
// is the customer's, so the blocks that stand it up live in the guide rather than
// here, where only the grant P0 needs is known.
const terraformHeader = (
  <Paragraph>
    Add the following to your Terraform and apply it, then click
    &quot;Next&quot;. If you have not deployed the connector yet, the{" "}
    <Link href={TERRAFORM_GUIDE_URL} target="_blank" rel="noreferrer">
      Terraform installation guide
    </Link>{" "}
    covers the whole deployment.
  </Paragraph>
);

type AzureHostingConfig = Extract<
  ElementConfigOf<typeof AppConnectorHosting>,
  { type: "azure" }
>;

// Entra checks a federated credential's issuer exactly against the `iss` of
// the Google ID token P0 presents.
const GOOGLE_ISSUER = "https://accounts.google.com";

const ENTRA_APPLICATION_DEVELOPER_URL =
  "https://learn.microsoft.com/en-us/entra/identity/role-based-access-control/permissions-reference#application-developer";
const AZURE_RBAC_ADMINISTRATOR_URL =
  "https://learn.microsoft.com/en-us/azure/role-based-access-control/built-in-roles/privileged#role-based-access-control-administrator";

/** The `azure` integration's root and `azure-app` installs, which an
 * Azure-hosted connector needs: P0 invokes it as the root install's service
 * account, and reads its Container App as the app registration. Undefined
 * unless both are installed. */
const azureInstallsFor = (installed: Record<string, unknown>) => {
  const azure = installed.azure as AzureIntegration | undefined;
  if (!azure) return undefined;
  const root = installedItem("root", azure, SINGLETON_ITEM);
  const app = installedItem("azure-app", azure, SINGLETON_ITEM);
  return root && app ? { app, root } : undefined;
};

const azureInstructions = (
  installed: Record<string, unknown>,
  id: string,
  hosting: AzureHostingConfig
): Instructions<ReactNode> => {
  // The option's prerequisites keep it from being chosen without both.
  const installs = azureInstallsFor(installed);
  if (!installs)
    throw new Error("The Microsoft Azure integration is not installed.");
  const { app, root } = installs;

  return {
    help: (
      <>
        <Paragraph>
          Register an app for your connector that trusts P0&apos;s service
          account, and let P0 read your connector&apos;s Container App, then
          click &quot;Next&quot;. P0 reads the Container App to get its URL and
          its app registration&apos;s client ID.
        </Paragraph>
        <Paragraph>
          To register the app, you need the Microsoft Entra{" "}
          <Link
            href={ENTRA_APPLICATION_DEVELOPER_URL}
            target="_blank"
            rel="noreferrer"
          >
            Application Developer
          </Link>{" "}
          role, or a directory that lets users register applications.
        </Paragraph>
        <Paragraph>
          To grant P0 Reader on the Container App, you need{" "}
          <Link
            href={AZURE_RBAC_ADMINISTRATOR_URL}
            target="_blank"
            rel="noreferrer"
          >
            Role Based Access Control Administrator
          </Link>{" "}
          or Owner on the Container App or a scope above it.
        </Paragraph>
      </>
    ),
    // Terraform only: the trust is several Entra and Azure resources that
    // reference each other's generated IDs.
    commands: {
      iac: [
        {
          header: (
            <>
              {terraformHeader}
              <Paragraph>
                It needs the <Text code>azuread</Text> and{" "}
                <Text code>azurerm</Text> providers, with{" "}
                <Text code>azurerm</Text> set to subscription{" "}
                <Text code>{hosting.subscriptionId}</Text>.
              </Paragraph>
            </>
          ),
          command: azureConnectorTrustTf({
            // Unique per install, so several connectors can share one
            // Terraform configuration.
            names: {
              resource: `custom_app_${id.replace(/\W/g, "_")}`,
              label: id,
            },
            containerApp: {
              type: "existing",
              name: hosting.connectorName,
              resourceGroupName: hosting.resourceGroup,
            },
            invoker: { issuer: GOOGLE_ISSUER, subject: root.serviceAccountId },
            readerPrincipalId: app.clientServicePrincipalId,
          }).trim(),
        },
      ],
    },
  };
};

const iamWriteInstructions: NonNullable<
  FrontendInstaller<
    typeof AppComponents
  >["components"]["iam-write"]["instructions"]
> = (context, id, item): Instructions<ReactNode> => {
  const { hosting } = item;
  if (hosting.type === "azure")
    return azureInstructions(context.installed, id, hosting);
  if (hosting.type === "aws") {
    const awsConfig = context.installed.aws as AwsIntegration | undefined;
    const partition = awsConfig
      ? awsPartitionFromUse(awsConfig, hosting.accountId, "iam-write")
      : "aws";
    const roleName = awsRoleName(context, "iam-write");
    const connectorArn = `arn:${partition}:lambda:${hosting.connectorRegion}:${hosting.accountId}:function:${hosting.connectorName}`;
    const policy = {
      Version: "2012-10-17",
      Statement: [
        {
          Sid: "InvokeCustomConnector",
          Effect: "Allow",
          Action: "lambda:InvokeFunction",
          Resource: connectorArn,
        },
      ],
    };

    return {
      help: (
        <Paragraph>
          Grant P0 permission to invoke your connector, then click
          &quot;Next&quot;. P0 does not need any other permission on this Lambda
          function.
        </Paragraph>
      ),
      commands: {
        console: (
          <>
            Grant the AWS role P0 assumes for this account{" "}
            <Text code>lambda:InvokeFunction</Text> on the Lambda function{" "}
            <Text code>{hosting.connectorName}</Text> in region{" "}
            <Text code>{hosting.connectorRegion}</Text>.
          </>
        ),
        shell: [
          {
            command: `
aws iam put-role-policy \\
  --role-name "${roleName}" \\
  --policy-name "InvokeCustomConnector" \\
  --policy-document '${JSON.stringify(policy, undefined, 2)}'
`.trim(),
          },
        ],
        iac: [
          {
            header: terraformHeader,
            command: `
resource "aws_iam_role_policy" "invoke_custom_connector" {
  name = "InvokeCustomConnector"
  role = "${roleName}"

  policy = jsonencode(${JSON.stringify(policy, undefined, 2)})
}
`.trim(),
          },
        ],
      },
    };
  }

  const gcloudConfig = context.installed.gcloud as
    | GcloudIntegration
    | undefined;
  const invokerServiceAccount =
    gcloudConfig?.root?.[SINGLETON_ITEM]?.serviceAccountEmail ?? "";

  return {
    help: (
      <Paragraph>
        Grant P0&apos;s service account permission to read and invoke your
        connector, then click &quot;Next&quot;. P0 reads the service to get its
        URL.
      </Paragraph>
    ),
    commands: {
      console: (
        <>
          Grant P0&apos;s Google Cloud service account the{" "}
          <Text code>roles/run.invoker</Text> and{" "}
          <Text code>roles/run.viewer</Text> roles on the Cloud Run service{" "}
          <Text code>{hosting.connectorName}</Text> in project{" "}
          <Text code>{hosting.projectId}</Text>, region{" "}
          <Text code>{hosting.connectorRegion}</Text>.
        </>
      ),
      shell: [
        {
          command: `
gcloud run services add-iam-policy-binding ${hosting.connectorName} \\
  --project=${hosting.projectId} \\
  --region=${hosting.connectorRegion} \\
  --member="serviceAccount:${invokerServiceAccount}" \\
  --role="roles/run.invoker"

${cloudRunViewerShell({
  service: hosting.connectorName,
  projectId: hosting.projectId,
  region: hosting.connectorRegion,
  member: invokerServiceAccount,
})}
`.trim(),
        },
      ],
      iac: [
        {
          header: terraformHeader,
          command: `
resource "google_cloud_run_v2_service_iam_member" "invoke_custom_connector" {
  project  = "${hosting.projectId}"
  name     = "${hosting.connectorName}"
  location = "${hosting.connectorRegion}"
  role     = "roles/run.invoker"
  member   = "serviceAccount:${invokerServiceAccount}"
}

${cloudRunViewerTf({
  resource: "view_custom_connector",
  project: `"${hosting.projectId}"`,
  location: `"${hosting.connectorRegion}"`,
  service: `"${hosting.connectorName}"`,
  member: invokerServiceAccount,
})}
`.trim(),
        },
      ],
    },
  };
};

export const appInstaller: FrontendInstaller<typeof AppComponents> = {
  components: {
    "iam-write": {
      instructions: iamWriteInstructions,
      items: {
        hosting: {
          options: {
            aws: {
              items: {
                accountId: {
                  optionProvider: otherInstalledIntegration({
                    integration: "aws",
                    component: "iam-write",
                  }),
                },
              },
            },
            azure: {
              prerequisiteMessages: async ({ installed }) => {
                if (!azureInstallsFor(installed))
                  return (
                    <div>
                      Install P0&apos;s Microsoft Azure integration and its
                      Azure app registration first: P0 invokes the connector as
                      the integration&apos;s service account, and reads its
                      Container App as the app registration.
                    </div>
                  );
              },
              items: {
                directoryId: {
                  // The root install is a singleton, so it offers its one
                  // directory.
                  optionProvider: async ({ installed }) => {
                    const installs = azureInstallsFor(installed);
                    return installs ? [{ id: installs.root.directoryId }] : [];
                  },
                },
              },
            },
            gcp: {
              items: {
                projectId: {
                  optionProvider: otherInstalledIntegration({
                    integration: "gcloud",
                    component: "iam-write",
                  }),
                },
              },
            },
          },
        },
      },
    },
  },
};
